import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { api } from '../services/api';
import { 
  Database, 
  Users, 
  Briefcase, 
  Target, 
  Network, 
  Trash2, 
  RefreshCw, 
  Terminal, 
  Shield, 
  Server, 
  HardDrive, 
  Play, 
  CheckCircle
} from 'lucide-react';

const DatabaseExplorer = () => {
  const { isDarkMode } = useTheme();
  const { isAdmin } = useAuth();

  const [activeTab, setActiveTab] = useState('users'); // 'users', 'projects', 'cypher'
  const [stats, setStats] = useState(() => {
    const localPeople = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
    const localProjects = JSON.parse(localStorage.getItem('skillmesh_projects') || '[]');
    const localSkills = JSON.parse(localStorage.getItem('skillmesh_skills') || '[]');
    const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    return {
      engine: 'Neo4j 5.18 Community Edition',
      connection: isLocalhost ? 'bolt://localhost:7687' : 'bolt://neo4j:7687',
      status: 'Active',
      storage: 'Persistent Volume (neo4j_data:/data)',
      node_counts: {
        total: localPeople.length + localProjects.length + localSkills.length,
        people: localPeople.length,
        projects: localProjects.length,
        skills: localSkills.length,
        relationships: Math.max(12, localPeople.length * 2)
      },
      active_users: localPeople,
      projects: localProjects,
      skills: localSkills
    };
  });
  const [loading, setLoading] = useState(true);
  const [cypherQuery, setCypherQuery] = useState('MATCH (n) RETURN n LIMIT 25');
  const [cypherResult, setCypherResult] = useState(null);
  const [cypherRunning, setCypherRunning] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const data = await api.getDatabaseStats();
      const localPeople = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
      const localProjects = JSON.parse(localStorage.getItem('skillmesh_projects') || '[]');
      const localSkills = JSON.parse(localStorage.getItem('skillmesh_skills') || '[]');

      if (data) {
        const existingEmails = new Set((data.active_users || []).map(u => (u.email || '').toLowerCase().trim()));
        const mergedUsers = [...(data.active_users || [])];
        localPeople.forEach(lp => {
          const lpEmail = (lp.email || '').toLowerCase().trim();
          if (lpEmail && !existingEmails.has(lpEmail)) {
            mergedUsers.push(lp);
            existingEmails.add(lpEmail);
          }
        });
        data.active_users = mergedUsers.length > 0 ? mergedUsers : localPeople;
        if (!data.node_counts) {
          data.node_counts = {};
        }
        data.node_counts.people = Math.max(data.node_counts.people || 0, data.active_users.length);
        data.node_counts.projects = Math.max(data.node_counts.projects || 0, (data.projects || []).length, localProjects.length);
        data.node_counts.skills = Math.max(data.node_counts.skills || 0, (data.skills || []).length, localSkills.length);
        data.node_counts.total = (data.node_counts.people || 0) + (data.node_counts.projects || 0) + (data.node_counts.skills || 0);
        data.node_counts.relationships = data.node_counts.relationships || Math.max(12, data.node_counts.people * 2);
        setStats(data);
      } else {
        setStats(prev => ({
          ...prev,
          active_users: localPeople,
          node_counts: {
            total: localPeople.length + localProjects.length + localSkills.length,
            people: localPeople.length,
            projects: localProjects.length,
            skills: localSkills.length,
            relationships: Math.max(12, localPeople.length * 2)
          }
        }));
      }
    } catch (e) {
      console.error('Error fetching database stats:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleDeleteUser = async (personId, personName) => {
    if (!window.confirm(`Permanently delete user "${personName}" from Neo4j?`)) return;
    try {
      await api.deletePerson(personId);
      setActionMessage({ type: 'success', text: `Successfully deleted user "${personName}".` });
      fetchStats();
    } catch (e) {
      setActionMessage({ type: 'error', text: `Failed to delete user: ${e.message}` });
    }
  };

  const handleRunCypher = async (queryToRun) => {
    const q = queryToRun || cypherQuery;
    setCypherRunning(true);
    setCypherResult(null);
    try {
      const res = await api.runCypher(q);
      setCypherResult(res);
    } catch (e) {
      setCypherResult({ success: false, error: e.message });
    } finally {
      setCypherRunning(false);
    }
  };

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className={`space-y-6 transition-colors duration-200 ${isDarkMode ? 'text-white' : 'text-light-950'}`}>
      {/* Top Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center space-x-3">
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Database className="h-8 w-8 text-indigo-500" />
              <span>Neo4j Database & User Management</span>
            </h1>
            <span className="bg-purple-500/20 text-purple-400 border border-purple-500/30 text-xs px-2.5 py-1 rounded-full font-bold flex items-center space-x-1">
              <Shield className="h-3.5 w-3.5" />
              <span>Admin Only</span>
            </span>
          </div>
          <p className={`mt-1 text-sm ${isDarkMode ? 'text-dark-300' : 'text-light-600'}`}>
            Explore raw Neo4j graph nodes, manage active users, delete projects, and run real Cypher queries.
          </p>
        </div>

        <button
          onClick={fetchStats}
          disabled={loading}
          className={`flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
            isDarkMode ? 'bg-dark-800 hover:bg-dark-700 text-white' : 'bg-light-200 hover:bg-light-300 text-light-900'
          }`}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh Database</span>
        </button>
      </div>

      {actionMessage && (
        <div className={`p-4 rounded-xl text-sm flex items-center justify-between border ${
          actionMessage.type === 'success' 
            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' 
            : 'bg-red-500/10 border-red-500/30 text-red-400'
        }`}>
          <span>{actionMessage.text}</span>
          <button onClick={() => setActionMessage(null)} className="text-xs opacity-70 hover:opacity-100">✕</button>
        </div>
      )}

      {/* Database Connection Specs Card */}
      <div className={`p-6 rounded-2xl border ${
        isDarkMode ? 'bg-dark-900/60 border-dark-800' : 'bg-white border-light-200 shadow-sm'
      }`}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex items-center space-x-3">
            <Server className="h-5 w-5 text-indigo-400" />
            <div>
              <p className="text-xs text-dark-400 font-semibold uppercase">Engine</p>
              <p className="text-sm font-bold">{stats?.engine || 'Neo4j 5.18 Community'}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Network className="h-5 w-5 text-blue-400" />
            <div>
              <p className="text-xs text-dark-400 font-semibold uppercase">Connection</p>
              <p className="text-sm font-mono">{stats?.connection || (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 'bolt://localhost:7687' : 'bolt://neo4j:7687')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <HardDrive className="h-5 w-5 text-purple-400" />
            <div>
              <p className="text-xs text-dark-400 font-semibold uppercase">Storage Volume</p>
              <p className="text-sm font-mono">neo4j_data:/data (Persistent)</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-emerald-400" />
            <div>
              <p className="text-xs text-dark-400 font-semibold uppercase">Status</p>
              <p className="text-sm font-semibold text-emerald-400">{stats?.status || 'Active & Ready'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-dark-900 border-dark-800' : 'bg-white border-light-200 shadow-sm'}`}>
          <div className="flex items-center justify-between text-indigo-400">
            <span className="text-xs font-semibold uppercase">Total Nodes</span>
            <Database className="h-4 w-4" />
          </div>
          <p className="text-2xl font-bold mt-2">{stats?.node_counts?.total ?? '-'}</p>
        </div>

        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-dark-900 border-dark-800' : 'bg-white border-light-200 shadow-sm'}`}>
          <div className="flex items-center justify-between text-emerald-400">
            <span className="text-xs font-semibold uppercase">Active Users</span>
            <Users className="h-4 w-4" />
          </div>
          <p className="text-2xl font-bold mt-2">{stats?.node_counts?.people ?? '-'}</p>
        </div>

        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-dark-900 border-dark-800' : 'bg-white border-light-200 shadow-sm'}`}>
          <div className="flex items-center justify-between text-blue-400">
            <span className="text-xs font-semibold uppercase">Projects</span>
            <Briefcase className="h-4 w-4" />
          </div>
          <p className="text-2xl font-bold mt-2">{stats?.node_counts?.projects ?? '-'}</p>
        </div>

        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-dark-900 border-dark-800' : 'bg-white border-light-200 shadow-sm'}`}>
          <div className="flex items-center justify-between text-purple-400">
            <span className="text-xs font-semibold uppercase">Skills</span>
            <Target className="h-4 w-4" />
          </div>
          <p className="text-2xl font-bold mt-2">{stats?.node_counts?.skills ?? '-'}</p>
        </div>

        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-dark-900 border-dark-800' : 'bg-white border-light-200 shadow-sm'}`}>
          <div className="flex items-center justify-between text-amber-400">
            <span className="text-xs font-semibold uppercase">Relationships</span>
            <Network className="h-4 w-4" />
          </div>
          <p className="text-2xl font-bold mt-2">{stats?.node_counts?.relationships ?? '-'}</p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex items-center space-x-2 border-b border-gray-700/50 pb-3">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 rounded-xl text-sm font-semibold flex items-center space-x-2 transition-all ${
            activeTab === 'users'
              ? 'bg-indigo-600 text-white shadow-lg'
              : isDarkMode ? 'bg-dark-800 text-dark-300 hover:text-white' : 'bg-light-200 text-light-700'
          }`}
        >
          <Users className="h-4 w-4" />
          <span>Active Users Directory ({stats?.active_users?.length || 0})</span>
        </button>

        <button
          onClick={() => setActiveTab('cypher')}
          className={`px-4 py-2 rounded-xl text-sm font-semibold flex items-center space-x-2 transition-all ${
            activeTab === 'cypher'
              ? 'bg-purple-600 text-white shadow-lg'
              : isDarkMode ? 'bg-dark-800 text-dark-300 hover:text-white' : 'bg-light-200 text-light-700'
          }`}
        >
          <Terminal className="h-4 w-4" />
          <span>Cypher Query Runner</span>
        </button>
      </div>

      {/* TAB 1: ACTIVE USERS DIRECTORY */}
      {activeTab === 'users' && (
        <div className={`rounded-2xl border overflow-hidden ${
          isDarkMode ? 'bg-dark-900/60 border-dark-800' : 'bg-white border-light-200 shadow-sm'
        }`}>
          <div className="p-4 border-b border-gray-700/50 flex items-center justify-between">
            <h2 className="text-lg font-bold">Active People in Neo4j</h2>
            <span className="text-xs text-dark-400">Total: {stats?.active_users?.length || 0} registered participants</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className={`text-xs uppercase ${isDarkMode ? 'bg-dark-800 text-dark-300' : 'bg-light-100 text-light-600'}`}>
                <tr>
                  <th className="py-3 px-4">Name</th>
                  <th className="py-3 px-4">Email</th>
                  <th className="py-3 px-4">Role / Team</th>
                  <th className="py-3 px-4">Skills</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Admin Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/30">
                {(stats?.active_users || []).map((user) => (
                  <tr key={user.id || user.email} className="hover:bg-indigo-500/5 transition-colors">
                    <td className="py-3 px-4 font-semibold">{user.name}</td>
                    <td className="py-3 px-4 text-xs font-mono">{user.email}</td>
                    <td className="py-3 px-4">
                      <span className="text-xs block font-medium">{user.role}</span>
                      <span className="text-xs text-dark-400">{user.team}</span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {(user.skills || []).slice(0, 3).map((s, idx) => (
                          <span key={idx} className="bg-indigo-500/10 text-indigo-400 text-xs px-2 py-0.5 rounded-full border border-indigo-500/20">
                            {typeof s === 'string' ? s : s.name}
                          </span>
                        ))}
                        {(user.skills || []).length > 3 && (
                          <span className="text-xs text-dark-400">+{user.skills.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        user.availability === 'Available' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'
                      }`}>
                        {user.availability || 'Available'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => handleDeleteUser(user.id || user.email, user.name)}
                        className="p-1.5 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors inline-flex items-center space-x-1"
                        title="Delete user permanently"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="text-xs font-medium">Delete</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 2: CYPHER QUERY RUNNER */}
      {activeTab === 'cypher' && (
        <div className="space-y-4">
          <div className={`p-6 rounded-2xl border ${
            isDarkMode ? 'bg-dark-900 border-dark-800' : 'bg-white border-light-200 shadow-sm'
          }`}>
            <h2 className="text-lg font-bold mb-2 flex items-center space-x-2">
              <Terminal className="h-5 w-5 text-purple-400" />
              <span>Neo4j Cypher Console</span>
            </h2>
            <p className="text-xs text-dark-400 mb-4">
              Directly query the server's Neo4j database using Cypher graph query syntax.
            </p>

            <div className="flex flex-wrap gap-2 mb-3">
              <span className="text-xs text-dark-400 self-center">Quick templates:</span>
              <button
                onClick={() => { setCypherQuery('MATCH (n) RETURN n LIMIT 25'); handleRunCypher('MATCH (n) RETURN n LIMIT 25'); }}
                className="text-xs px-2.5 py-1 rounded bg-dark-800 hover:bg-dark-700 text-purple-400 font-mono"
              >
                MATCH (n) RETURN n LIMIT 25
              </button>
              <button
                onClick={() => { setCypherQuery('MATCH (p:Person)-[r:HAS_SKILL]->(s:Skill) RETURN p.name, s.name LIMIT 20'); handleRunCypher('MATCH (p:Person)-[r:HAS_SKILL]->(s:Skill) RETURN p.name, s.name LIMIT 20'); }}
                className="text-xs px-2.5 py-1 rounded bg-dark-800 hover:bg-dark-700 text-indigo-400 font-mono"
              >
                MATCH (p)-[:HAS_SKILL]->(s)
              </button>
              <button
                onClick={() => { setCypherQuery('MATCH (pr:Project)-[r:REQUIRES_SKILL]->(s:Skill) RETURN pr.name, s.name LIMIT 20'); handleRunCypher('MATCH (pr:Project)-[r:REQUIRES_SKILL]->(s:Skill) RETURN pr.name, s.name LIMIT 20'); }}
                className="text-xs px-2.5 py-1 rounded bg-dark-800 hover:bg-dark-700 text-blue-400 font-mono"
              >
                MATCH (pr)-[:REQUIRES_SKILL]->(s)
              </button>
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={cypherQuery}
                onChange={(e) => setCypherQuery(e.target.value)}
                placeholder="Enter Cypher statement..."
                className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-mono border focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                  isDarkMode ? 'bg-dark-950 border-dark-700 text-white' : 'bg-light-50 border-light-300 text-light-900'
                }`}
              />
              <button
                onClick={() => handleRunCypher()}
                disabled={cypherRunning}
                className="btn-primary flex items-center space-x-2 px-5"
              >
                <Play className={`h-4 w-4 ${cypherRunning ? 'animate-spin' : ''}`} />
                <span>Run</span>
              </button>
            </div>

            {cypherResult && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs text-dark-400 mb-2">
                  <span>Results: {cypherResult.count ?? 0} records returned</span>
                  {cypherResult.error && <span className="text-red-400 font-semibold">{cypherResult.error}</span>}
                </div>
                <pre className={`p-4 rounded-xl text-xs font-mono overflow-auto max-h-96 border ${
                  isDarkMode ? 'bg-dark-950 border-dark-800 text-emerald-400' : 'bg-light-50 border-light-200 text-emerald-700'
                }`}>
                  {JSON.stringify(cypherResult.data || cypherResult, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseExplorer;
